import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Box, CircularProgress } from '@mui/material';
import type { Permission } from '@pd/shared';
import { useAuth } from './AuthContext';

export function FullPageLoader() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh' }}>
      <CircularProgress />
    </Box>
  );
}

/** Signed-in area. Forces the password change before anything else when required. */
export function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/hyr" replace state={{ from: location.pathname }} />;
  // The route state tells the page it was sent here for the first-login change (see there).
  const forcedHere =
    location.pathname === '/ndrysho-fjalekalimin' &&
    (location.state as { forced?: boolean } | null)?.forced;
  if (user.mustChangePassword && !forcedHere) {
    return <Navigate to="/ndrysho-fjalekalimin" replace state={{ forced: true }} />;
  }
  return <Outlet />;
}

/** Only for signed-out users (login, reset). */
export function GuestOnly() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  return user ? <Navigate to="/" replace /> : <Outlet />;
}

export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <Navigate to="/" replace />;
}

/** Conditionally renders UI for users holding a permission. */
export function Can({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : null;
}
